/**
 * calc/cashFlow.ts — the cash-flow kernel shared by every portfolio engine.
 *
 * Houses the ONE canonical cash-rule (cashDelta) that holdings, the equity-curve
 * engine, and the chart's value-series all funnel through, so the arithmetic can
 * never drift between them. No dependency on other calc/ modules (keeps the graph acyclic).
 */
import { fxRateFor, resolveTradeCurrency, type FxStore } from './fx';
import { amountRowSign, isAmountRow } from './tradeTypes';

/**
 * Optional currency-conversion context. When OMITTED no conversion happens and
 * behaviour is byte-for-byte identical to the single-currency code.
 */
export interface FxContext { store: FxStore; base: string }

/**
 * The trade fields the market-valuation engine needs. Deliberately looser than
 * the strict `Trade` so the ONE engine serves both the dashboard and the chart.
 */
export interface ReplayTrade {
  date: string;
  ticker: string;
  type: string;
  shares: number;
  price: number;
  amount?: number;
  commission?: number;
  currency?: string;
  /** A FEE or TAX row that gives the cost back (calc/tradeTypes.ts). */
  refund?: boolean;
}

/**
 * Base-currency magnitude of an amount row's cash (DEPOSIT, WITHDRAWAL, FEE,
 * INTEREST, TAX; FX at the row's date, a blank currency being the base). Non-positive / non-finite amounts contribute nothing.
 */
export function cashFlowBase(
  t: { amount?: number; currency?: string; date: string },
  fx?: FxContext,
): number {
  const amt = t.amount ?? 0;
  if (!Number.isFinite(amt) || amt <= 0) return 0;
  return amt * fxRateFor(fx, t.currency, t.date);
}

/**
 * Cash impact of a single trade or cash flow on a running balance — the ONE
 * place the cash rules live, so every caller shares the same arithmetic.
 *
 *   BUY        -> -(shares*price + commission)
 *   SELL       -> +(shares*price - commission)
 *   DIVIDEND   -> +(shares*price - commission)   (as received; position untouched)
 *   DEPOSIT    -> +amount
 *   WITHDRAWAL -> -amount
 *   INTEREST   -> +amount   (income, inside the return)
 *   FEE        -> -amount   (a charge, inside the return: net of fees)
 *   TAX        -> -amount   (tax withheld on income, inside the return)
 *   a FEE or TAX with refund: true -> +amount (the cost given back)
 *
 * `ccyOverride` lets an engine that resolves FX per-ticker keep cash on the same
 * rate it values holdings at; the default is the trade's own currency.
 *
 * There is no dividend factor here, on purpose (the owner's word of
 * 2026-10-12): the book's DIVIDEND is credited as received and its TAX rows
 * debit the tax withheld, so a page's market dividend tax (core/
 * dividendTreatment `divFactor`, which treats market series only) can never
 * tax the book a second time. Until then a `dividendFactor` argument scaled
 * the DIVIDEND credit here, and every book page passed the page's rate.
 */
export function cashDelta(
  t: ReplayTrade,
  fx?: FxContext,
  ccyOverride?: string,
): number {
  // A stock SPLIT never moves cash (it only rescales the share count / per-share cost).
  if (t.type === 'SPLIT') return 0;
  // Amount rows: the type gives the direction, `amount` the magnitude, in the
  // row's own currency (never the per-ticker override: a FEE attributed to a
  // ticker is still charged in the currency it was written in).
  if (isAmountRow(t.type)) return amountRowSign(t) * cashFlowBase(t, fx);
  const ccy = ccyOverride || resolveTradeCurrency(t);
  const rate = fxRateFor(fx, ccy, t.date);
  const gross = t.shares * t.price * rate;
  const commission = (t.commission ?? 0) * rate;
  return t.type === 'BUY' ? -(gross + commission) : gross - commission;
}
